import {
    type ApprovedWhatsAppTemplateParameters,
    type TemplateVariableSource,
    WHATSAPP_TEMPLATE_PARAMETER_KINDS,
} from 'cpool-constants';
import type { ValueOf } from 'cpool-utils';

export const WHATSAPP_TEMPLATE_SEGMENT_KINDS = {
    NAMED_PLACEHOLDER: 'namedPlaceholder',
    POSITIONAL_PLACEHOLDER: 'positionalPlaceholder',
    TEXT: 'text',
} as const;

export type WhatsAppTemplateSegmentKind = ValueOf<typeof WHATSAPP_TEMPLATE_SEGMENT_KINDS>;

export type TemplateSegment =
    | { kind: typeof WHATSAPP_TEMPLATE_SEGMENT_KINDS.TEXT; text: string }
    | {
          kind: typeof WHATSAPP_TEMPLATE_SEGMENT_KINDS.NAMED_PLACEHOLDER;
          name: string;
          variable: TemplateVariableSource;
      }
    | { kind: typeof WHATSAPP_TEMPLATE_SEGMENT_KINDS.POSITIONAL_PLACEHOLDER; index: number };

export const NAMED_PLACEHOLDER_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
export const POSITIONAL_PLACEHOLDER_PATTERN = /\{\{\s*(\d+)\s*\}\}/g;

// Unrecognized / out-of-range `{{tokens}}` stay as literal text, not dropped.
export function parseTemplateSegments(
    text: string,
    parameters: ApprovedWhatsAppTemplateParameters,
): Array<TemplateSegment> {
    if (parameters.kind === WHATSAPP_TEMPLATE_PARAMETER_KINDS.NONE) {
        return text ? [{ kind: WHATSAPP_TEMPLATE_SEGMENT_KINDS.TEXT, text }] : [];
    }

    const pattern =
        parameters.kind === WHATSAPP_TEMPLATE_PARAMETER_KINDS.POSITIONAL
            ? POSITIONAL_PLACEHOLDER_PATTERN
            : NAMED_PLACEHOLDER_PATTERN;

    const { lastIndex, segments } = Array.from(text.matchAll(pattern)).reduce<{
        lastIndex: number;
        segments: Array<TemplateSegment>;
    }>(
        (acc, match) => {
            const matchIndex = match.index ?? 0;
            const textBefore: Array<TemplateSegment> =
                matchIndex > acc.lastIndex
                    ? [{ kind: WHATSAPP_TEMPLATE_SEGMENT_KINDS.TEXT, text: text.slice(acc.lastIndex, matchIndex) }]
                    : [];

            let placeholderSegment: TemplateSegment;
            if (parameters.kind === WHATSAPP_TEMPLATE_PARAMETER_KINDS.POSITIONAL) {
                const index = Number(match[1]);
                placeholderSegment =
                    index >= 1 && index <= parameters.count
                        ? { index, kind: WHATSAPP_TEMPLATE_SEGMENT_KINDS.POSITIONAL_PLACEHOLDER }
                        : { kind: WHATSAPP_TEMPLATE_SEGMENT_KINDS.TEXT, text: match[0] };
            } else {
                const name = match[1] as string;
                const variable = parameters.variables[name];
                placeholderSegment = variable
                    ? { kind: WHATSAPP_TEMPLATE_SEGMENT_KINDS.NAMED_PLACEHOLDER, name, variable }
                    : { kind: WHATSAPP_TEMPLATE_SEGMENT_KINDS.TEXT, text: match[0] };
            }

            return {
                lastIndex: matchIndex + match[0].length,
                segments: [...acc.segments, ...textBefore, placeholderSegment],
            };
        },
        { lastIndex: 0, segments: [] },
    );

    const trailingText: Array<TemplateSegment> =
        lastIndex < text.length ? [{ kind: WHATSAPP_TEMPLATE_SEGMENT_KINDS.TEXT, text: text.slice(lastIndex) }] : [];

    return [...segments, ...trailingText];
}
